<template>
  <div :class="$style.ctn">
    <Stats :class="$style.stats" :values="stats" />
    <h3 :class="[com.subtitle, $style.title]">
      HTTP requests
    </h3>
    <PaginationTable
      :class="$style.table"
      :head="requestsHead"
      :values="requests"
      not-found-message="No HTTP requests"
      :pagination-detach="true"
      :table-wrapper-class="media.networkTableWrapper"
    >
      <template #cell="{ cell, i }">
        <b v-if="i === 0">
          {{ cell }}
        </b>
        <template v-else-if="i === 7 || i === 9">
          <span :class="$style.badge">
            <Badge :value="cell.value" :type="cell.type" :full-width="true" />
          </span>
        </template>
      </template>
      <template #footer>
        <TipMessage :class="$style.tipMessage" :full-report-link-at-end="true">
          Download PCAP, analyze network streams, HTTP content and a lot more at the
        </TipMessage>
      </template>
    </PaginationTable>
    <h3 :class="[com.subtitle, $style.title]">
      Connections
    </h3>
    <PaginationTable
      :class="$style.table"
      :head="connectionsHead"
      :values="connections"
      :pagination-detach="true"
      :table-wrapper-class="media.connectionTableWrapper"
    >
      <template #cell="{ cell, i }">
        <b v-if="i === 0">
          {{ cell }}
        </b>
        <template v-else-if="i === 6">
          <span :class="$style.badge">
            <Badge :value="cell.value" :type="cell.type" :full-width="true" />
          </span>
        </template>
      </template>
    </PaginationTable>
    <h3 :class="[com.subtitle, $style.title]">
      DNS requests
    </h3>
    <PaginationTable
      :class="[$style.table, $style.tableDNS]"
      :head="dnsHead"
      :values="dns"
      :pagination-detach="true"
      :table-wrapper-class="media.dnsTableWrapper"
    >
      <template #cell="{ cell, i }">
        <b v-if="i === 0">
          {{ cell }}
        </b>
        <ValueCell v-if="i === 1" :class="$style.ipList" :value="cell" />
        <template v-else-if="i === 2">
          <span :class="$style.badge">
            <Badge :value="cell.value" :type="cell.type" :full-width="true" />
          </span>
        </template>
      </template>
    </PaginationTable>
    <h3 :class="[com.subtitle, $style.title]">
      Threats
    </h3>
    <PaginationTable
      :not-found-message="
        threatsProCount ? 'Found threats are available for the paid subscriptions' : 'No threats detected'
      "
      :class="$style.table"
      :head="threatsHead"
      :values="threats"
      :pagination-detach="true"
      :table-wrapper-class="media.threatsTableWrapper"
    >
      <template #cell="{ cell, i }">
        <b v-if="i === 0">
          {{ cell }}
        </b>
        <template v-else-if="i === 2">
          <span :class="$style.badge">
            <Badge :value="cell.value" :type="cell.type" />
          </span>
        </template>
      </template>

      <template #footer>
        <TipMessage
          v-if="threatsProCount"
          :class="$style.tipMessage"
          :full-report-link-at-end="true"
        >
          {{ threatsProCount }} ETPRO signatures available at the
        </TipMessage>
      </template>
    </PaginationTable>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

import Badge from './Badge.vue';
import PaginationTable from './PaginationTable.vue';
import Stats from './Stats.vue';
import TipMessage from './TipMessage.vue';
import ValueCell from './ValueCell.vue';

export default defineComponent({
  components: {
    Stats,
    PaginationTable,
    TipMessage,
    Badge,
    ValueCell,
  },
  inject: ['root'],
  data: () => ({
    requestsHead: [
      { value: 'PID', width: 58 },
      { value: 'Process', width: 106 },
      { value: 'Method', width: 60 },
      { value: 'HTTP Code', width: 80 },
      { value: 'IP', width: 140 },
      { value: 'URL', width: 318 },
      { value: 'CN', width: 68 },
      { value: 'Type', width: 89 },
      { value: 'Size', width: 63 },
      { value: 'Reputation', width: 98 },
    ],
    connectionsHead: [
      { value: 'PID', width: 58 },
      { value: 'Process', width: 166 },
      { value: 'IP', width: 220 },
      { value: 'Domain', width: 214 },
      { value: 'ASN', width: 261 },
      { value: 'CN', width: 63 },
      { value: 'Reputation', width: 98 },
    ],
    dnsHead: [
      { value: 'Domain', width: 228 },
      { value: 'IP', },
      { value: 'Reputation', width: 98 },
    ],

    threatsHead: [
      { value: 'PID', width: 58 },
      { value: 'Process', width: 170 },
      { value: 'Class', width: 286 },
      { value: 'Message', width: 566 },
    ],
  }),
  computed: {
    requests() {
      return this.root.networkActivity.requests;
    },
    connections() {
      return this.root.networkActivity.connections;
    },
    stats() {
      return this.root.networkActivity.stats;
    },
    dns() {
      return this.root.networkActivity.dns;
    },
    threats() {
      return this.root.networkActivity.threats;
    },
    threatsProCount() {
      return this.root.networkActivity.threatsProCount;
    },
  },
});
</script>

<style lang="scss" module>
.stats + .title {
  margin-top: 40px;
}
.title + .table {
  margin-top: 15px;
}
.table + .title {
  margin-top: 40px;
}
.tableDNS {
  z-index: 2;
  position: relative;
}
.ctn {
  padding-top: 20px;
}
.badge {
  margin-top: -2px;
  display: inline-block;
  width: 100%;
}

* + .tipMessage {
  margin-top: 15px;
}
.tipMessage + * {
  margin-top: 20px;
}

.table {
  --padding-left-right: 14px;
}
.ipList {
  & > li {
    & + li {
      margin-top: 6px;
    }
  }
}
</style>

<style lang="scss" src="./common.module.scss" module="com"></style>
<style lang="scss" src="./media.module.scss" module="media"></style>
